/** Wordle-style shareable result text. */
import type { GradeCategory, GradeReport, ResultSummary } from '../core/contracts';
import { goalCoverage, type MatchExtras } from './progress';

const LETTER_SQUARE: Record<GradeReport['letter'], string> = { S: '🟪', A: '🟥', B: '🟧', C: '🟨', D: '🟦', F: '⬛' };
export const CATEGORY_ORDER: readonly GradeCategory[] = ['coverage', 'shot_choice', 'pacing', 'continuity', 'replays', 'graphics', 'booth'];

/** 2_412_000 → "2.4M", 18_400 → "18K", 950 → "950", 999_800 → "1M" (never "1000K"). */
export function formatCompact(n: number): string {
  const a = Math.abs(n);
  const f = (v: number, s: string) => `${v >= 100 ? Math.round(v) : (Math.round(v * 10) / 10).toFixed(v >= 10 ? 0 : 1).replace(/\.0$/, '')}${s}`;
  if (a >= 999.5e6) return f(n / 1e9, 'B');
  if (a >= 999.5e3) return f(n / 1e6, 'M');
  if (a >= 999.5) return f(n / 1e3, 'K');
  return String(Math.round(n));
}

export function categorySquare(v: number): string {
  return v >= 75 ? '🟩' : v >= 50 ? '🟨' : '🟥';
}

export interface ShareContext {
  dailyNumber?: number;
  /** e.g. "Career · National" when not a daily. */
  modeLabel?: string;
  url?: string;
}

export function buildShareText(summary: ResultSummary, ctx: ShareContext = {}, extras: MatchExtras = {}): string {
  const r = summary.report;
  const head = ctx.dailyNumber ? `SPORTS DIRECTOR — Daily #${ctx.dailyNumber}` : `SPORTS DIRECTOR — ${ctx.modeLabel ?? `Seed ${summary.seed}`}`;
  const { covered, goals } = goalCoverage(summary);
  const peak = Math.max(extras.peakViewers ?? 0, r.viewers);
  const line = `${LETTER_SQUARE[r.letter]} ${r.letter} (${Math.round(r.rating)}) ⚽ ${covered}/${goals} goals covered 📺 ${formatCompact(peak)} viewers`;
  const cats = CATEGORY_ORDER.map((c) => categorySquare(r.byCategory[c] ?? 0)).join('');
  const match = `${summary.homeName} ${summary.score.home}–${summary.score.away} ${summary.awayName}`;
  return [head, line, cats, match, ctx.url].filter(Boolean).join('\n');
}
